/**
 * Phase 22 — Auth context + guarded route component.
 *
 * The access token is a JWT stored in `localStorage` under `clasifi_token`
 * and attached to every request via `Authorization: Bearer <token>` in
 * `lib/api.js`. On 401 the api layer dispatches `clasifi:401` which the
 * provider listens for to clear the session.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

const AuthContext = createContext(null);
const TOKEN_KEY = 'clasifi_token';
const USER_KEY = 'clasifi_user';

function readStoredUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(() => readStoredUser());

  // Keep storage in sync.
  useEffect(() => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  }, [token]);
  useEffect(() => {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  }, [user]);

  // Hard reset on 401 from any API call.
  useEffect(() => {
    const onUnauth = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener('clasifi:401', onUnauth);
    return () => window.removeEventListener('clasifi:401', onUnauth);
  }, []);

  // On mount with an existing token, refresh the user profile so profile
  // fields (display_name, language, timezone, appearance) are present even
  // after a page reload.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const BACKEND_URL2 = process.env.REACT_APP_BACKEND_URL || '';
        const r = await fetch(`${BACKEND_URL2}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok && !cancelled) {
          const full = await r.json();
          setUser((prev) => ({ ...(prev || {}), ...full }));
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // Only rehydrate on token change (not user); user is a derived read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = useCallback(async (email, password) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
    const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const raw = await res.text();
    if (!res.ok) {
      let detail = 'Invalid email or password';
      try {
        const p = JSON.parse(raw);
        detail = typeof p.detail === 'string' ? p.detail : (p.detail?.[0]?.msg || JSON.stringify(p.detail));
      } catch { /* ignore */ }
      const err = new Error(detail);
      err.status = res.status;
      throw err;
    }
    const data = JSON.parse(raw);
    const u = { email: data.email, role: data.role, name: data.name };
    setToken(data.access_token);
    setUser(u);
    // Fire-and-forget hydration of the full profile so Settings/Preferences
    // can render without a flash of defaults. If this fails (eg. during
    // tests), the context still has the basic login payload.
    try {
      const BACKEND_URL2 = process.env.REACT_APP_BACKEND_URL || '';
      const r2 = await fetch(`${BACKEND_URL2}/api/auth/me`, { headers: { Authorization: `Bearer ${data.access_token}` } });
      if (r2.ok) {
        const full = await r2.json();
        setUser({ ...u, ...full });
        return { ...u, ...full };
      }
    } catch { /* ignore */ }
    return u;
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  const value = { user, token, login, logout, updateUser, isAuthed: Boolean(token && user) };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/**
 * Guard a route tree. `role` can be `"admin"` to require the admin role, or
 * omitted to accept any authenticated user. Unauthenticated hits redirect to
 * `/login?redirect=<path>`; wrong-role hits redirect to `/app` so the user
 * doesn't see a dead end.
 */
export function RequireAuth({ role, children }) {
  const { user, token } = useAuth();
  const location = useLocation();
  if (!token || !user) {
    const to = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
    return <Navigate to={to} replace />;
  }
  if (role === 'admin' && user.role !== 'admin') {
    return <Navigate to="/app" replace />;
  }
  return children;
}

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Hard logout — synchronously drops the token from storage and does a full
 * page navigation to `nextUrl`. Avoids the race between React Router state
 * commits and AuthProvider state clears that caused logout to bounce through
 * `/login?redirect=/app`. For programmatic (non-user-initiated) logout, use
 * `logout()` from `useAuth()` instead.
 */
export function hardLogout(nextUrl = '/') {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch { /* ignore */ }
  window.location.assign(nextUrl);
}
